import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { KeyRound, Eye, EyeOff, Building2, Loader2 } from 'lucide-react';
import { API_BASE_URL, DEV_AUTH_ENABLED, PUBLIC_SIGNUP_ENABLED } from '@/utils/runtime';
import { setAuthSession } from '@/utils/auth';
import { BeaconLogo, BeaconWordmark } from '@/components/BeaconBrand';

export const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ssoError = searchParams.get('sso_error');

  // ── Login state ────────────────────────────────────────────────────────────
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState(ssoError ? `SSO error: ${ssoError.replace(/_/g, ' ')}` : '');
  const [loading, setLoading]           = useState(false);

  // ── SSO state ─────────────────────────────────────────────────────────────
  const [ssoMode, setSsoMode]           = useState(false);
  const [ssoEmail, setSsoEmail]         = useState('');
  const [ssoLoading, setSsoLoading]     = useState(false);
  const [ssoError2, setSsoError2]       = useState('');

  const handleSSOSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSsoLoading(true);
    setSsoError2('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/v1/sso/init`, { email: ssoEmail });
      // Redirect browser to IdP
      window.location.href = data.authorization_url;
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setSsoError2(typeof detail === 'string' ? detail : 'No SSO provider found for that email domain.');
      setSsoLoading(false);
    }
  };

  // ── New-password challenge state ───────────────────────────────────────────
  const [challenge, setChallenge]         = useState<'NEW_PASSWORD_REQUIRED' | null>(null);
  const [session, setSession]             = useState<string>('');
  const [newPassword, setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew]             = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // ── Dev bypass (DISABLE_AUTH) — owner/member share one seeded tenant ────────
  const handleDevLogin = (seat: 'owner' | 'member') => {
    const tokens = seat === 'owner'
      ? {
          access: 'beacon-dev-owner-token',
          id: 'beacon-dev-owner-id-token',
          refresh: 'beacon-dev-owner-refresh-token',
        }
      : {
          access: 'beacon-dev-member-token',
          id: 'beacon-dev-member-id-token',
          refresh: 'beacon-dev-member-refresh-token',
        };

    setAuthSession({
      accessToken: tokens.access,
      idToken: tokens.id,
      refreshToken: tokens.refresh,
    });
    navigate('/');
  }

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/v1/auth/login`, { email, password });

      if (data.challenge === 'NEW_PASSWORD_REQUIRED') {
        // User was created by an admin — they must set a new password first
        setSession(data.session ?? '');
        setChallenge('NEW_PASSWORD_REQUIRED');
        return;
      }

      setAuthSession({
        accessToken: data.access_token,
        idToken: data.id_token,
        refreshToken: data.refresh_token,
      });
      navigate('/');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 401) setError('Incorrect email or password');
      else if (err.response?.status === 400) setError(detail || 'Login failed');
      else setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setChangingPassword(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/v1/auth/complete-new-password`, {
        email,
        new_password: newPassword,
        session,
      });

      setAuthSession({
        accessToken: data.access_token,
        idToken: data.id_token,
        refreshToken: data.refresh_token,
      });
      navigate('/');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Failed to set new password. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  // ── Set-new-password screen ────────────────────────────────────────────────
  if (challenge === 'NEW_PASSWORD_REQUIRED') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto mb-3 w-fit">
            <BeaconLogo size={84} />
          </div>
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-beacon-orange/15 mb-4">
            <KeyRound className="h-6 w-6 text-beacon-navy" />
          </div>
            <h2 className="text-3xl font-extrabold text-gray-900">Set your password</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your account was created by an admin. Please set a permanent password to continue.
            </p>
          </div>

          <form className="mt-8 space-y-5" onSubmit={handleCompleteNewPassword}>
            {error && (
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">{error}</div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm pr-10 focus:outline-none focus:ring-beacon-orange focus:border-beacon-orange"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowNew(!showNew)}
                >
                  {showNew ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-beacon-orange focus:border-beacon-orange"
                placeholder="Repeat your new password"
              />
            </div>

            <button
              type="submit"
              disabled={changingPassword}
              className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-beacon-navy hover:bg-beacon-navy/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-beacon-orange disabled:opacity-50"
            >
              {changingPassword ? 'Saving…' : 'Set password & sign in'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Normal login screen ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto mb-3 w-fit">
            <BeaconLogo size={84} />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to <BeaconWordmark className="font-extrabold" textClassName="text-gray-900" />
          </h2>
          <p className="mt-2 text-center text-sm text-gray-500">
            BeaCon - Personal AWS Assistant
          </p>
          {PUBLIC_SIGNUP_ENABLED ? (
            <p className="mt-2 text-center text-sm text-gray-600">
              Or{' '}
              <Link to="/signup" className="font-medium text-beacon-navy hover:text-beacon-orange">
                create a new account
              </Link>
            </p>
          ) : (
            <p className="mt-2 text-center text-sm text-gray-600">
              Use your company invite, SSO, or AWS Marketplace onboarding to access Beacon.
            </p>
          )}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          )}

          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">Email address</label>
              <input
                id="email-address"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-beacon-orange focus:border-beacon-orange focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-beacon-orange focus:border-beacon-orange focus:z-10 sm:text-sm pr-10"
                placeholder="Password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center z-20"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword
                  ? <EyeOff className="h-5 w-5 text-gray-400" />
                  : <Eye className="h-5 w-5 text-gray-400" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link to="/forgot-password" className="font-medium text-beacon-navy hover:text-beacon-orange">
                Forgot your password?
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-beacon-navy hover:bg-beacon-navy/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-beacon-orange disabled:opacity-50"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {/* SSO divider */}
          <div className="relative mt-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          {!ssoMode ? (
            <button
              type="button"
              onClick={() => setSsoMode(true)}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <Building2 className="h-4 w-4" />
              Sign in with SSO
            </button>
          ) : (
            <form onSubmit={handleSSOSubmit} className="space-y-3">
              {ssoError2 && (
                <p className="text-sm text-red-600 text-center">{ssoError2}</p>
              )}
              <input
                type="email"
                placeholder="you@yourcompany.com"
                value={ssoEmail}
                onChange={e => setSsoEmail(e.target.value)}
                required
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-beacon-orange focus:border-beacon-orange sm:text-sm"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSsoMode(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={ssoLoading || !ssoEmail}
                  className="flex-1 flex items-center justify-center gap-1 py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-beacon-navy hover:bg-beacon-navy/90 disabled:opacity-50"
                >
                  {ssoLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : 'Continue'}
                </button>
              </div>
            </form>
          )}

        {/* Dev-only login shortcuts — hidden in production */}
        {DEV_AUTH_ENABLED && (
          <div className="mt-6 border border-dashed border-amber-300 rounded-lg p-4 bg-amber-50">
            <p className="text-xs font-semibold text-amber-700 mb-1 text-center uppercase tracking-wide">
              Dev mode — multi-seat smoke
            </p>
            <p className="text-[11px] text-amber-800/80 mb-3 text-center">
              Owner and member share one tenant. Or send header{' '}
              <code className="font-mono">X-Beacon-Dev-User: owner|member</code>.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleDevLogin('owner')}
                className="flex-1 py-2 px-3 text-xs font-medium rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors"
              >
                Login as Owner
              </button>
              <button
                onClick={() => handleDevLogin('member')}
                className="flex-1 py-2 px-3 text-xs font-medium rounded-md border border-amber-400 text-amber-700 hover:bg-amber-100 transition-colors"
              >
                Login as Member
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
